// ─────────────────────────────────────────────────────────────────────
// Access Management ▸ Employee Role Assignment tab — Round 5 (2026).
//
// Embedded version of the standalone /admin/employee-roles page. Reuses the
// SAME backend endpoints (EmployeeRoleAdminController):
//   GET /api/admin/employees-with-roles
//   PUT /api/admin/employees/:empId/role   { roleId | null }
// and the SAME role list as the rest of Access Management.
//
// Round 1 added the last-IT-Admin / disabled-role guards on the assign
// endpoint; this tab surfaces a refusal via a toast (server is the boundary).
//
// Differences from the standalone page: no page-level chrome (the Access
// Management shell already provides header/container), and only ACTIVE roles
// are offered in the dropdown (a disabled role can't be newly assigned —
// matching the Round 1 server guard). The standalone page remains available
// and untouched.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Input, Button, Badge, Spinner, Table, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchActiveRoles, fetchEmployeesWithRoles, assignEmployeeRole,
} from "../../../api/accessManagement";

export default function EmployeeAssignmentTab() {
    const [roles, setRoles]         = useState([]);   // active roles only
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch]       = useState("");

    const [pendingRole, setPendingRole] = useState({}); // { [empId]: roleIdString | "" }
    const [savingId, setSavingId]       = useState(null);

    const load = async () => {
        setLoading(true); setLoadError(null);
        try {
            const [r, e] = await Promise.all([
                fetchActiveRoles(),
                fetchEmployeesWithRoles(),
            ]);
            setRoles(r);
            setEmployees(e);
        } catch (err) {
            const s = err?.response?.status;
            setLoadError(
                s === 401 || s === 403
                    ? "You are not authorized to view this."
                    : (err?.response?.data?.error || "Failed to load employees/roles.")
            );
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) => {
            const hay = [
                e.empId, e.firstName, e.lastName, e.name, e.company,
                e.jcDept, e.department, e.roleName,
                e.roleId == null ? "unassigned" : String(e.roleId),
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [employees, search]);

    // ── Client-side pagination (the whole list is loaded up front) ──
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const totalRows = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    useEffect(() => { setPage(1); }, [search, pageSize]);
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * pageSize;
    const paged = filtered.slice(pageStart, pageStart + pageSize);

    const handleSave = async (emp) => {
        const raw = pendingRole[emp.empId];
        const newRoleId = raw === "" ? null : Number(raw);

        if (raw !== "" && Number.isNaN(newRoleId)) {
            toast.error("Invalid role selection.");
            return;
        }
        if (newRoleId === (emp.roleId ?? null)) {
            toast.info("No change.");
            return;
        }

        setSavingId(emp.empId);
        try {
            const updated = await assignEmployeeRole(emp.empId, newRoleId);
            setEmployees((list) =>
                list.map((e) => e.empId === emp.empId
                    ? { ...e, roleId: updated.roleId ?? null, roleName: updated.roleName ?? null }
                    : e
                )
            );
            setPendingRole((p) => {
                const next = { ...p };
                delete next[emp.empId];
                return next;
            });
            toast.success(
                newRoleId == null
                    ? `Cleared role for ${emp.empId}.`
                    : `Updated ${emp.empId} to ${updated.roleName ?? "role " + newRoleId}.`
            );
        } catch (err) {
            // 400 surfaces the last-IT-Admin / disabled-role guard message.
            const s = err?.response?.status;
            toast.error(
                err?.response?.data?.error ||
                (s === 403 ? "Not authorized to update roles." : "Failed to update role.")
            );
        } finally {
            setSavingId(null);
        }
    };

    if (loading && employees.length === 0) {
        return <div className="text-center text-muted py-5"><Spinner size="sm" /> Loading employees…</div>;
    }
    if (loadError) {
        return <div className="alert alert-danger">{loadError}</div>;
    }

    return (
        <>
            <Row className="g-2 align-items-center mb-3">
                <Col md={8}>
                    <Input
                        type="search"
                        bsSize="sm"
                        placeholder="Search EmpID, name, company, dept, role…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col md={4} className="text-md-end">
                    <span className="text-muted small me-2">{filtered.length} employees</span>
                    <Button color="light" size="sm" onClick={load} disabled={loading}>
                        <i className="ri-refresh-line align-bottom me-1" />Refresh
                    </Button>
                </Col>
            </Row>

            <div className="table-responsive">
                <Table className="align-middle table-nowrap table-hover mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Emp ID</th>
                            <th>Name</th>
                            <th>Company</th>
                            <th>Dept</th>
                            <th>Status</th>
                            <th>Current Role</th>
                            <th style={{ minWidth: 220 }}>Assign Role</th>
                            <th className="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-muted py-4 small">
                                {search ? "No matches." : "No employees found."}
                            </td></tr>
                        ) : paged.map((e) => {
                            const pending = pendingRole[e.empId];
                            const selectValue =
                                pending !== undefined ? pending : (e.roleId == null ? "" : String(e.roleId));
                            const dirty =
                                pending !== undefined &&
                                Number(pending || NaN) !== (e.roleId ?? NaN) &&
                                !(pending === "" && e.roleId == null);
                            return (
                                <tr key={e.empId}>
                                    <td><code>{e.empId}</code></td>
                                    <td>{e.name || `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() || <span className="text-muted">—</span>}</td>
                                    <td>{e.company || <span className="text-muted">—</span>}</td>
                                    <td>{e.jcDept || e.department || <span className="text-muted">—</span>}</td>
                                    <td>{e.empStatus ? <Badge color="light" className="text-dark">{e.empStatus}</Badge> : <span className="text-muted">—</span>}</td>
                                    <td>
                                        {e.roleId == null
                                            ? <Badge color="warning">Unassigned</Badge>
                                            : <Badge color="info">{e.roleName ?? `Role ${e.roleId}`}</Badge>}
                                    </td>
                                    <td>
                                        <Input
                                            type="select"
                                            bsSize="sm"
                                            value={selectValue}
                                            onChange={(ev) => setPendingRole((p) => ({ ...p, [e.empId]: ev.target.value }))}
                                            disabled={savingId === e.empId}
                                        >
                                            <option value="">— Unassigned —</option>
                                            {/* If the employee's current role is disabled it won't be in
                                                the active list; show it as a locked current value so the
                                                dropdown isn't blank/misleading. */}
                                            {e.roleId != null && !roles.some((r) => r.roleId === e.roleId) && (
                                                <option value={e.roleId}>
                                                    {(e.roleName ?? `Role ${e.roleId}`)} (current, disabled)
                                                </option>
                                            )}
                                            {roles.map((r) => (
                                                <option key={r.roleId} value={r.roleId}>
                                                    {r.roleName} (#{r.roleId})
                                                </option>
                                            ))}
                                        </Input>
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            color={dirty ? "primary" : "light"}
                                            size="sm"
                                            disabled={!dirty || savingId === e.empId}
                                            onClick={() => handleSave(e)}
                                        >
                                            {savingId === e.empId
                                                ? <><Spinner size="sm" /> Saving…</>
                                                : <><i className="ri-save-line align-bottom me-1" />Save</>}
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                {/* Pagination controls */}
                {totalRows > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                        <div className="text-muted small">
                            Showing {pageStart + 1}–{Math.min(pageStart + pageSize, totalRows)} of {totalRows}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">Rows per page</span>
                            <Input
                                type="select"
                                bsSize="sm"
                                style={{ width: 80 }}
                                value={pageSize}
                                onChange={(ev) => setPageSize(Number(ev.target.value))}
                            >
                                {[10, 25, 50, 100].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </Input>
                            <Pagination size="sm" className="mb-0">
                                <PaginationItem disabled={safePage <= 1}>
                                    <PaginationLink first onClick={() => setPage(1)} />
                                </PaginationItem>
                                <PaginationItem disabled={safePage <= 1}>
                                    <PaginationLink previous onClick={() => setPage(safePage - 1)} />
                                </PaginationItem>
                                <PaginationItem active>
                                    <PaginationLink>{safePage} / {totalPages}</PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={safePage >= totalPages}>
                                    <PaginationLink next onClick={() => setPage(safePage + 1)} />
                                </PaginationItem>
                                <PaginationItem disabled={safePage >= totalPages}>
                                    <PaginationLink last onClick={() => setPage(totalPages)} />
                                </PaginationItem>
                            </Pagination>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
